import {
    autoUpdate,
    FloatingPortal,
    flip,
    offset,
    shift,
    size,
    useFloating,
} from "@floating-ui/react";
import {
    faChevronDown,
    faExclamationTriangle,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDebounce } from "@uidotdev/usehooks";
import type { Point } from "geojson";
import { useEffect, useMemo, useState } from "react";
import type { Site } from "../../apiClient/generated";
import { SearchInput } from "../../ui/Inputs";
import {
    centerOfPoints,
    compassBearing,
    distanceToSite,
    formatDistance,
} from "../../utils/geopatialUtils";
import { useNearbySites } from "./nearbySites";

const FAR_SITE_DISTANCE_METERS = 250;

/**
 * furthestDistanceToSite
 * Kinda self-explanatory.
 *
 * @param site
 * @param points
 * @returns
 */
const furthestDistanceToSite = (site: Site, points: Point[]): number | null => {
    const distances = points
        .map((point) => distanceToSite(site, point))
        .filter((distance): distance is number => distance !== null);
    return distances.length ? Math.max(...distances) : null;
};

const RadioDot = ({ checked }: { checked: boolean }) => (
    <div
        className={`shrink-0 h-4 w-4 rounded-full border ${
            checked
                ? "border-ae-blue-550 border-[5px]"
                : "border-ae-gray-300 bg-white"
        }`}
    />
);

const SiteBadge = ({
    label,
    tone,
}: {
    label: string;
    tone: "info" | "warn";
}) => (
    <span
        className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${
            tone === "warn"
                ? "bg-orange-50 text-orange-800"
                : "bg-ae-blue-40 text-ae-blue-900"
        }`}
    >
        {label}
    </span>
);

const siteCoordinates = (site: Site) =>
    site.location?.coordinates
        ? `(${site.location.coordinates[1].toFixed(4)}, ${site.location.coordinates[0].toFixed(4)})`
        : "";

interface SitePickerProps {
    detectionLocations: Point[];
    selectedSite: Site | null;
    onSelect: (site: Site) => void;
    error?: string;
}

export const SitePicker = (props: SitePickerProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search, 400);

    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [
            offset({ mainAxis: 4 }),
            flip(),
            shift({ padding: 8 }),
            size({
                apply: ({ rects, elements }) => {
                    elements.floating.style.width = `${rects.reference.width}px`;
                },
            }),
        ],
        whileElementsMounted: autoUpdate,
    });

    useEffect(() => {
        if (!open) {
            return;
        }
        const onMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            const reference = refs.reference.current as HTMLElement | null;
            const floating = refs.floating.current;
            if (reference?.contains(target) || floating?.contains(target)) {
                return;
            }
            setOpen(false);
        };
        document.addEventListener("mousedown", onMouseDown);
        return () => document.removeEventListener("mousedown", onMouseDown);
    }, [open, refs.reference, refs.floating]);

    // The site list and the bearings measure from a single point.
    const center = useMemo(
        () => centerOfPoints(props.detectionLocations),
        [props.detectionLocations],
    );

    // The API orders by distance but doesn't serialize it, so the distances
    // shown below are computed client side.
    const sitesQuery = useNearbySites(center, debouncedSearch);

    const sites = useMemo(
        () =>
            (sitesQuery.data || []).map((site) => ({
                site,
                distance: furthestDistanceToSite(
                    site,
                    props.detectionLocations,
                ),
                bearing:
                    site.location && center
                        ? compassBearing(center, site.location as Point)
                        : "",
            })),
        [sitesQuery.data, props.detectionLocations, center],
    );

    // Warns as soon as one detection is too far from the picked site.
    const { selectedDistance, farCount } = useMemo(() => {
        if (!props.selectedSite) {
            return { selectedDistance: null, farCount: 0 };
        }
        const distances = props.detectionLocations
            .map((point) => distanceToSite(props.selectedSite, point))
            .filter((distance): distance is number => distance !== null);
        return {
            selectedDistance: distances.length ? Math.max(...distances) : null,
            farCount: distances.filter(
                (distance) => distance > FAR_SITE_DISTANCE_METERS,
            ).length,
        };
    }, [props.selectedSite, props.detectionLocations]);
    const selectedIsFar = farCount > 0;

    return (
        <div className="text-sm flex flex-col gap-1.5">
            <p className="text-neutral-500">
                Associate{" "}
                {props.detectionLocations.length > 1
                    ? `these ${props.detectionLocations.length} detections`
                    : "this detection"}{" "}
                with a site: <span className="text-red-500">*</span>
            </p>

            <button
                ref={refs.setReference}
                type="button"
                onClick={() => setOpen(!open)}
                className={`w-full flex items-center gap-3 rounded border px-3 py-2 bg-white text-left ${
                    props.error ? "border-red-500" : "border-ae-gray-250"
                }`}
            >
                <span
                    className={`truncate ${
                        props.selectedSite
                            ? "text-ae-slate-900"
                            : "text-neutral-400"
                    }`}
                >
                    {props.selectedSite
                        ? `${props.selectedSite.siteName} · ${siteCoordinates(props.selectedSite)}`
                        : "Select a site…"}
                </span>
                <span className="flex-1" />
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className="h-3.5 w-3.5 shrink-0 text-neutral-500"
                />
            </button>

            {open && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        // The enclosing Headless UI Dialog closes on clicks
                        // outside its panel, which this list is.
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                        className="z-[1100] text-sm rounded border border-ae-gray-250 bg-white shadow-xl overflow-hidden"
                    >
                        <div className="p-2 border-b border-ae-slate-200">
                            <SearchInput
                                value={search}
                                placeholder="Search by site name or site ID…"
                                onChange={setSearch}
                                autoFocus
                            />
                        </div>
                        <p className="px-3 pt-2 pb-1 text-xs font-bold uppercase tracking-wide text-neutral-500">
                            {debouncedSearch
                                ? "Matching company sites"
                                : "Nearest company sites"}
                        </p>
                        <div className="max-h-52 overflow-y-auto">
                            {sitesQuery.isLoading && (
                                <p className="px-3 py-3 text-neutral-500">
                                    Loading sites…
                                </p>
                            )}
                            {!sitesQuery.isLoading && sites.length === 0 && (
                                <p className="px-3 py-3 text-neutral-500">
                                    No sites found.
                                </p>
                            )}
                            {sites.map(({ site, distance, bearing }, index) => {
                                const selected =
                                    props.selectedSite?.id === site.id;
                                return (
                                    <button
                                        type="button"
                                        key={site.id}
                                        onClick={() => {
                                            props.onSelect(site);
                                            setOpen(false);
                                        }}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-t border-ae-slate-200 ${
                                            selected
                                                ? "bg-ae-blue-100"
                                                : "hover:bg-ae-slate-100"
                                        }`}
                                    >
                                        <RadioDot checked={selected} />
                                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                                            <div className="flex items-baseline gap-2 flex-wrap">
                                                <span className="font-semibold text-ae-slate-900">
                                                    {site.siteName}
                                                </span>
                                                {index === 0 &&
                                                    !debouncedSearch && (
                                                        <SiteBadge
                                                            label="Nearest site"
                                                            tone="info"
                                                        />
                                                    )}
                                                {!!site.endDate && (
                                                    <SiteBadge
                                                        label="Inactive — End Date set"
                                                        tone="warn"
                                                    />
                                                )}
                                            </div>
                                            <span className="text-xs text-neutral-500 truncate">
                                                {site.operatorUniqueId ||
                                                    site.id}
                                                {siteCoordinates(site)
                                                    ? ` · ${siteCoordinates(site)}`
                                                    : ""}
                                            </span>
                                        </div>
                                        <div className="shrink-0 text-right flex flex-col">
                                            <span className="font-semibold text-ae-slate-600">
                                                {formatDistance(distance)}
                                            </span>
                                            <span className="text-xs text-neutral-400">
                                                {bearing}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </FloatingPortal>
            )}

            {selectedIsFar && (
                <div className="flex items-start gap-2 rounded px-2 py-1 text-xs bg-yellow-200 text-yellow-900">
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="mt-0.5 shrink-0 text-yellow-700"
                    />
                    <span>
                        {props.detectionLocations.length > 1
                            ? `${farCount} of the ${props.detectionLocations.length} selected detections ${farCount === 1 ? "is" : "are"} over ${formatDistance(FAR_SITE_DISTANCE_METERS)} from ${props.selectedSite.siteName} (up to ${formatDistance(selectedDistance)} away).`
                            : `${props.selectedSite.siteName} is ${formatDistance(selectedDistance)} from this detection.`}{" "}
                        Double-check this is the right site before saving.
                    </span>
                </div>
            )}

            {!!props.error && <p className="text-red-500">{props.error}</p>}
        </div>
    );
};
